import React, { useState, useEffect } from 'react';
import { 
    TextField, 
    Button, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Pagination, 
    Box, 
    MenuItem, 
    Select, 
    InputLabel, 
    FormControl 
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/it';  // Import Italian locale
import dayjs from 'dayjs';
import axios from 'axios';

const PaginatedResults = () => {
    const [startDateTime, setStartDateTime] = useState(null);
    const [stopDateTime, setStopDateTime] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [hasUsedFilter, setHasUsedFilter] = useState(false);

    useEffect(() => {
        document.title = "Ricerca immagini SmartLeaf";
        fetchResults();
    }, []);

    const fetchResults = async (page = currentPage) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: itemsPerPage,
            };

            if (startDateTime) params.start = startDateTime.toISOString();
            if (stopDateTime) params.stop = stopDateTime.toISOString();
            if (searchText) params.address = searchText;

            const response = await axios.get('/api/v0/records', { params });
            setResults(response.data.records);
            setTotalPages(Math.ceil(response.data.total_count / itemsPerPage));
        } catch (error) {
            console.error('Errore nel recupero dei risultati:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (startDateTime || stopDateTime || searchText) {
            setHasUsedFilter(true);
        }
        setCurrentPage(1);
        fetchResults(1);
    };

    const handleExport = () => {
        try {
            const params = new URLSearchParams();
            if (startDateTime) params.append('start', startDateTime.toISOString());
            if (stopDateTime) params.append('stop', stopDateTime.toISOString());
            if (searchText) params.append('address', searchText);

            const exportUrl = `/api/v0/export?${params.toString()}`;
            window.open(exportUrl, '_blank');
        } catch (error) {
            console.error('Errore durante l\'esportazione dei dati:', error);
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        fetchResults(value);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    return (
        <div style={{ padding: '20px' }}>
            <Box display="flex" justifyContent="center" marginBottom="20px">
                <img src="/logo.png" alt="Logo" style={{ height: '100px' }} />
            </Box>

            <h1 style={{ textAlign: 'center' }}>Ricerca immagini SmartLeaf</h1>

            <LocalizationProvider dateAdapter={AdapterDayjs} locale="it">
                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                    <DateTimePicker
                        label="Data/Ora Inizio"
                        value={startDateTime}
                        onChange={(newValue) => setStartDateTime(newValue)}
                        format="DD/MM/YYYY HH:mm"
                        ampm={false}
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <DateTimePicker
                        label="Data/Ora Fine"
                        value={stopDateTime}
                        onChange={(newValue) => setStopDateTime(newValue)}
                        format="DD/MM/YYYY HH:mm"
                        ampm={false}
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <TextField
                        label="Testo di ricerca"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        fullWidth
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={loading}>
                        {loading ? 'Caricamento...' : 'Cerca'}
                    </Button>
                    <Button variant="outlined" onClick={handleExport} disabled={!hasUsedFilter || loading}>
                        Esporta
                    </Button>
                </div>
            </LocalizationProvider>

            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="20px">
                <FormControl>
                    <InputLabel id="items-per-page-label">Elementi per pagina</InputLabel>
                    <Select
                        labelId="items-per-page-label"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        style={{ minWidth: '150px' }}
                    >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={1000}>1000</MenuItem>
                    </Select>
                </FormControl>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID File</TableCell>
                            <TableCell>Data/Ora</TableCell>
                            <TableCell>Latitudine</TableCell>
                            <TableCell>Longitudine</TableCell>
                            <TableCell>Indirizzo</TableCell>
                            <TableCell>Azioni</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.id}</TableCell>
                                <TableCell>{dayjs(row.timestamp).locale('it').format('DD/MM/YYYY HH:mm')}</TableCell>
                                <TableCell>{row.latitude.toFixed(5)}</TableCell>
                                <TableCell>{row.longitude.toFixed(5)}</TableCell>
                                <TableCell>{row.address || 'N/D'}</TableCell>
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        href={`/api/v0/view/${row.id}`}
                                        target="_blank"
                                    >
                                        Visualizza
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default PaginatedResults;

